import {
  createRootRouteWithContext,
  HeadContent,
  Link,
  Outlet,
  Scripts,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { MapIcon } from "lucide-react";
import { Navigation } from "@/components/navigation";
import { MaintenanceBanner } from "@/components/maintenance-banner";
import { Footer } from "@/components/footer";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { ContainerSmall } from "@/components/primitives/container-small";
import { PageContainer } from "@/components/primitives/layout";
import { Providers } from "@/app/providers";
import "@fontsource-variable/figtree/index.css";
import figtreeLatinUrl from "@fontsource-variable/figtree/files/figtree-latin-wght-normal.woff2?url";
import "@/app/globals.css";
import faviconSvgUrl from "@/assets/icons/favicon.svg?url";
import faviconPngUrl from "@/assets/icons/favicon.png?url";
import appleIconUrl from "@/assets/icons/apple-icon.png?url";
import { getAllChangelogEntries } from "@/lib/content/changelog";
import { getMaintenanceBannerEnabled } from "@/lib/features/getMaintenanceBannerEnabled";
import { getCanonicalUrl } from "@/lib/seo";
import { createServerFn } from "@tanstack/react-start";
const getRecentChangelog = createServerFn({ method: "GET" }).handler(
  async () => {
    return getAllChangelogEntries().slice(0, 3);
  },
);
const getMaintenanceBanner = createServerFn({ method: "GET" }).handler(
  async () => {
    return getMaintenanceBannerEnabled();
  },
);
interface RouterContext {
  queryClient: QueryClient;
}
export const Route = createRootRouteWithContext<RouterContext>()({
  head: ({ matches }) => {
    const canonicalUrl = getCanonicalUrl(matches.at(-1)?.pathname ?? "/");
    return {
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        { title: "Scoopulator: An approachable ice cream calculator" },
        {
          name: "description",
          content:
            "Make your best ice cream with Scoopulator. Calculate composition, sweetness, and scoopability of your ice cream with scientific precision.",
        },
        { name: "theme-color", content: "#E08733" },
        { property: "og:type", content: "website" },
        {
          property: "og:title",
          content: "Scoopulator: An approachable ice cream calculator",
        },
        { property: "og:site_name", content: "Scoopulator" },
        {
          property: "og:description",
          content:
            "Make your best ice cream with Scoopulator. Calculate composition, sweetness, and scoopability of your ice cream with scientific precision.",
        },
        { property: "og:image", content: "/logo.png" },
        { property: "og:url", content: canonicalUrl },
      ],
      links: [
        { rel: "canonical", href: canonicalUrl },
        {
          rel: "preload",
          href: figtreeLatinUrl,
          as: "font",
          type: "font/woff2",
          crossOrigin: "anonymous",
        },
        { rel: "icon", href: "/favicon.ico", sizes: "any" },
        { rel: "icon", href: faviconSvgUrl, type: "image/svg+xml" },
        { rel: "icon", href: faviconPngUrl, type: "image/png" },
        { rel: "apple-touch-icon", href: appleIconUrl },
        { rel: "manifest", href: "/manifest.json" },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Scoopulator",
            description:
              "Make your best ice cream with Scoopulator, a powerful yet approachable ice cream calculator",
            url: "https://scoopulator.app",
          }).replace(/</g, "\\u003c"),
        },
      ],
    };
  },
  loader: async () => {
    const [recentChangelog, maintenanceBannerEnabled] = await Promise.all([
      getRecentChangelog(),
      getMaintenanceBanner(),
    ]);
    return { recentChangelog, maintenanceBannerEnabled };
  },
  component: RootComponent,
  shellComponent: RootDocument,
  notFoundComponent: NotFoundComponent,
  shouldReload: false,
});
function NotFoundComponent() {
  return (
    <PageContainer className="py-16">
      <ContainerSmall>
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <MapIcon />
            </EmptyMedia>
            <EmptyTitle>Page not found</EmptyTitle>
            <EmptyDescription>
              The page you&apos;re looking for doesn&apos;t exist.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button render={<Link to="/" />}>Go home</Button>
              <Button variant="outline" render={<Link to="/calc" />}>
                Calculator
              </Button>
            </div>
          </EmptyContent>
        </Empty>
      </ContainerSmall>
    </PageContainer>
  );
}
function RootComponent() {
  const { recentChangelog, maintenanceBannerEnabled } = Route.useLoaderData();
  return (
    <Providers>
      <MaintenanceBanner enabled={maintenanceBannerEnabled} />
      <Navigation recentChangelog={recentChangelog} />
      <main className="min-h-0">
        <Outlet />
      </main>
      <Footer />
      <Toaster position="bottom-left" />
    </Providers>
  );
}
function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body className="grid min-h-dvh grid-cols-1 grid-rows-[auto_auto_1fr_auto] font-sans antialiased">
        {children}
        <Scripts />
      </body>
    </html>
  );
}
